import { api } from '@/shared/api';
import type { ApiResponse } from '@/shared/types/api';

export interface CreateCheckoutSessionInput {
  houseId: string;
  invoiceId?: string;
  paymentType?:
    | 'RENT'
    | 'MAINTENANCE'
    | 'OTHER'
    | 'ONBOARDING'
    | 'HOUSE_TRANSFER'
    | 'SUBSCRIPTION';
}

export interface SubscriptionCheckoutSessionInput {
  packageId: string;
  paymentType: 'SUBSCRIPTION';
}

export interface CheckoutSessionResponse {
  clientSecret: string;
  house: {
    id: string;
    name: string;
    price: number;
    currency: string;
  };
  invoiceId?: string;
  url?: string; // Add url here as well if needed
}

export interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: 'month' | 'year';
  displayFeatures: string[];
  capabilities: string[];
  maxResidents?: number;
  maxAdmins?: number;
  isPopular?: boolean; // Frontend helper
}

export interface StripeConnectResponse {
  url: string;
}

export interface SubscriptionCheckoutResponse {
  clientSecret: string | null;
  package: Package;
  subscriptionId: string;
}

export const paymentApis = {
  createCheckoutSession: async (
    data: CreateCheckoutSessionInput,
  ): Promise<CheckoutSessionResponse> => {
    const res = await api.post<ApiResponse<CheckoutSessionResponse>>(
      '/payments/checkout-session',
      data,
    );
    return res.data;
  },

  createSubscriptionCheckoutSession: async (
    data: SubscriptionCheckoutSessionInput,
  ): Promise<SubscriptionCheckoutResponse> => {
    const res = await api.post<ApiResponse<SubscriptionCheckoutResponse>>(
      '/payments/subscription-checkout-session',
      data,
    );
    return res.data;
  },

  toggleAutoBilling: async (
    enable: boolean,
  ): Promise<{ isAutoBillingEnabled: boolean }> => {
    const res = await api.put<ApiResponse<{ isAutoBillingEnabled: boolean }>>(
      '/payments/auto-billing',
      { enable },
    );

    return res.data;
  },

  getAutoBillingStatus: async (): Promise<{
    isAutoBillingEnabled: boolean;
  }> => {
    const res = await api.get<ApiResponse<{ isAutoBillingEnabled: boolean }>>(
      '/payments/auto-billing',
    );
    return res.data;
  },

  connectToStripe: async (): Promise<ApiResponse<StripeConnectResponse>> => {
    const response = await api.get<ApiResponse<StripeConnectResponse>>(
      '/payments/stripe/connect/oauth',
    );
    return response;
  },

  stripeConnectCallback: async (
    code: string,
    state: string,
  ): Promise<ApiResponse<null>> => {
    const response = await api.post<ApiResponse<null>>(
      '/payments/stripe/connect/oauth/callback',
      { code, state },
    );
    return response;
  },

  getStripeConnectStatus: async (): Promise<
    ApiResponse<{ status: string; date: string }>
  > => {
    const response = await api.get<
      ApiResponse<{ status: string; date: string }>
    >('/payments/stripe/connect/status');
    return response;
  },

  getPackages: async (): Promise<Package[]> => {
    const response = await api.get<ApiResponse<Package[]>>('/packages');
    return response.data;
  },
};
